import { useMemo, useState } from 'react';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import type {
    ComingDataItem,
    LeadCloudFileType,
    SupplierRequestFullType,
    WarehouseComingRowType,
} from '@econtractor/types';
import * as apiSupplierRequests from 'api/apiSupplierRequests';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { safeFloatParse, safeIntParse } from 'utils/helpers/safeParsing';
import type { SupplierRequestDraftItem } from './types';

interface UseSupplierRequestComingParams {
    requestId: number | null;
    items: SupplierRequestDraftItem[];
    canOps: boolean;
    isActive: boolean;
    hasRemaining: boolean;
    /** Дата створення заявки (ms) → у опис операції приходу */
    requestDt?: number | null;
    onSuccess: (request: SupplierRequestFullType) => void;
    onListChanged: () => void;
}

const useSupplierRequestComing = ({
    requestId,
    items,
    canOps,
    isActive,
    hasRemaining,
    requestDt = null,
    onSuccess,
    onListChanged,
}: UseSupplierRequestComingParams) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const canCredit = Boolean(params.creditControl);
    const rate = Number(localSettings?.RATE?.current || 1) || 1;

    const [showDraft, setShowDraft] = useState(false);
    const [draftComing, setDraftComing] = useState<WarehouseComingRowType[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingCloudFiles, setPendingCloudFiles] = useState<File[]>([]);
    const [cloudProgress, setCloudProgress] = useState<number | boolean>(false);

    const remainingByProduct = useMemo(() => {
        const map = new Map<number, number>();
        for (const item of items) {
            const productId = safeIntParse(item.productId) || 0;
            const remaining =
                (safeFloatParse(item.itemCount) || 0) -
                (safeFloatParse(item.receivedCount) || 0);
            if (productId > 0 && remaining > 0) map.set(productId, remaining);
        }
        return map;
    }, [items]);

    const canStartComing = canOps && isActive && hasRemaining && !isSubmitting;

    const buildComingDescription = () => {
        const dt = DT.fromMillis(Number(requestDt || 0));
        const date = dt.isValid ? dt.toFormat('dd.MM.yyyy') : '—';
        return t('supplierRequests.comingAutoDescription', { date });
    };

    const buildDraftFromRemaining = () => {
        const cDescription = buildComingDescription();
        const rows: WarehouseComingRowType[] = items
            .map((item) => {
                const productId = safeIntParse(item.productId) || 0;
                const remaining =
                    (safeFloatParse(item.itemCount) || 0) -
                    (safeFloatParse(item.receivedCount) || 0);
                if (!(productId > 0) || !(remaining > 0)) return null;
                const price = safeFloatParse(item.prodDefPrice) || 0;
                const priceSale = safeFloatParse(item.prodDefPriceSale) || 0;
                return {
                    setId: 0,
                    product: {
                        ID: productId,
                        value: productId,
                        label: String(item.prodName || ''),
                        vendor: String(item.vendorName || ''),
                        article: String(item.prodArticle || ''),
                        defPrice: price,
                        defPriceSale: priceSale,
                    },
                    coming: {
                        cCount: remaining,
                        cDescription,
                        price,
                        priceRate: safeFloatParse(price * rate) || 0,
                        priceSale,
                        priceSaleRate: safeFloatParse(priceSale * rate) || 0,
                        payment: canCredit,
                        paymentDescription: '',
                        paymentAttachmentsCloud: [],
                    },
                } as WarehouseComingRowType;
            })
            .filter(Boolean) as WarehouseComingRowType[];
        return rows;
    };

    const clearPendingFiles = () => {
        setPendingCloudFiles([]);
        setCloudProgress(false);
    };

    const handleOpenDraft = () => {
        if (!canStartComing) return;
        const rows = buildDraftFromRemaining();
        if (!rows.length) {
            showToast(t('supplierRequests.messNoRemaining'), 'warning');
            return;
        }
        clearPendingFiles();
        setDraftComing(rows);
        setShowDraft(true);
    };

    const handleCancelDraft = () => {
        if (isSubmitting || cloudProgress !== false) return;
        setShowDraft(false);
        setDraftComing([]);
        clearPendingFiles();
    };

    const getMaxCount = (row: WarehouseComingRowType) => {
        const productId = safeIntParse(row?.product?.ID || row?.product?.value) || 0;
        const max = remainingByProduct.get(productId);
        return max != null ? max : null;
    };

    const uploadCloudFiles = async (productId: number): Promise<LeadCloudFileType[]> => {
        if (!pendingCloudFiles.length) return [];

        const filesMeta = pendingCloudFiles.map((file) => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
        }));

        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, {
            productId: Number(productId || 0),
        });
        if (!uploadConfigs || uploadConfigs.length !== pendingCloudFiles.length) {
            throw new Error('Upload URLs are not available');
        }

        const uploaded = await uploadFilesWithOverallProgress(
            pendingCloudFiles,
            uploadConfigs,
            setCloudProgress,
        );
        setCloudProgress(100);
        return uploaded.filter((item) => item.success).map((item) => item.config);
    };

    const handleConduct = async () => {
        const id = safeIntParse(requestId) || 0;
        if (!id || isSubmitting || cloudProgress !== false || !draftComing.length) return;

        const validated: Array<{
            row: WarehouseComingRowType;
            productId: number;
            count: number;
            payment: boolean;
            price: number;
            priceRate: number;
        }> = [];

        for (const row of draftComing) {
            const productId = safeIntParse(row.product?.ID || row.product?.value) || 0;
            const count = safeFloatParse(row.coming?.cCount) || 0;
            const max = remainingByProduct.get(productId) ?? 0;
            if (!(productId > 0) || !(count > 0)) {
                showToast(t('supplierRequests.messInputCount'), 'warning');
                return;
            }
            if (count > max + 1e-9) {
                showToast(t('supplierRequests.messComingExceeds'), 'warning');
                return;
            }
            const payment = canCredit && Boolean(row.coming?.payment);
            const price = safeFloatParse(row.coming?.price) || 0;
            const priceRate =
                safeFloatParse(row.coming?.priceRate ?? price * rate) || 0;
            validated.push({ row, productId, count, payment, price, priceRate });
        }

        const hasPaid = validated.some((item) => item.payment);
        let paymentAttachmentsCloud: LeadCloudFileType[] = [];

        setIsSubmitting(true);
        try {
            if (hasPaid && pendingCloudFiles.length) {
                const firstPaidProductId =
                    validated.find((item) => item.payment)?.productId || 0;
                try {
                    setCloudProgress(1);
                    paymentAttachmentsCloud = await uploadCloudFiles(firstPaidProductId);
                    if (paymentAttachmentsCloud.length !== pendingCloudFiles.length) {
                        showToast(t('common:messError'), 'warning');
                    }
                } catch (e) {
                    console.error(e);
                    showToast(t('common:messError'), 'danger');
                    return;
                } finally {
                    setCloudProgress(false);
                }
            }

            const payload: ComingDataItem[] = validated.map(
                ({ row, productId, count, payment, price, priceRate }) => ({
                    productId,
                    count,
                    description: String(row.coming?.cDescription || '').trim(),
                    price,
                    priceRate,
                    priceSale: safeFloatParse(row.coming?.priceSale) || 0,
                    priceSaleRate: safeFloatParse(row.coming?.priceSaleRate) || 0,
                    payment,
                    paymentAmount: payment ? count * price : 0,
                    paymentAmountRate: payment ? count * priceRate : 0,
                    paymentDescription: payment
                        ? String(row.coming?.paymentDescription || '').trim()
                        : '',
                    paymentAttachments: [],
                    paymentAttachmentsCloud: payment ? paymentAttachmentsCloud : [],
                    setId: safeIntParse(row.setId) || 0,
                }),
            );

            const result = await apiSupplierRequests.createComing(id, payload);
            if (!result?.stat || !result.request) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            showToast(t('supplierRequests.messComingDone'), 'success');
            setShowDraft(false);
            setDraftComing([]);
            clearPendingFiles();
            onSuccess(result.request);
            onListChanged();
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setIsSubmitting(false);
            setCloudProgress(false);
        }
    };

    return {
        canCredit,
        canStartComing,
        showDraft,
        draftComing,
        setDraftComing,
        isSubmitting,
        pendingCloudFiles,
        setPendingCloudFiles,
        cloudProgress,
        getMaxCount,
        handleOpenDraft,
        handleCancelDraft,
        handleConduct,
    };
};

export default useSupplierRequestComing;
